import seedrandom from 'seedrandom';

import { ConvertableToDateTime, TimeUnit } from '@cast/shared-types';

import { timeRange } from './date';
import { shiftNumber } from './number';

let currentCount = 1;

export const pseudoUnique = (): number => {
  if (Number.MAX_SAFE_INTEGER === currentCount) {
    currentCount = 1;
    return currentCount;
  }
  return ++currentCount;
};

type RandomizerArgs = {
  min?: number;
  max?: number;
  fractional?: boolean;
};

export type CreateRandomizerArgs = {
  token?: string;
  options?: RandomizerArgs;
};

type GenerateRandomNumber = (args?: RandomizerArgs) => number;

export type Randomizer = {
  random: GenerateRandomNumber;
  randomElement: <T>(array: T[]) => T;
  /**
   * @deprecated use shiftNumber instead
   */
  randomWithBase: (
    base: number,
    offset: number,
    options?: CreateRandomizerArgs['options']
  ) => number;
};

export const createRandomizer = ({
  token = 'seed',
  options: { min = 0, max = 1, fractional } = {},
}: CreateRandomizerArgs = {}): Randomizer => {
  const random = seedrandom(token);

  const validate = (min: number, max: number): void => {
    if (min > max) {
      throw Error('"Min" value cannot exceed or be higher than the "Max"');
    }
  };

  const generateRandom: GenerateRandomNumber = (options): number => {
    const _min = options?.min || min;
    const _max = options?.max || max;
    validate(_min, _max);

    const distance = Math.abs(_min - _max);
    const isFractional = options?.fractional ?? fractional;
    const num = random() * distance + _min;
    if (isFractional) {
      return num;
    }
    return Math.round(num);
  };

  return {
    random: generateRandom,
    randomElement: <T>(array: T[]): T => {
      if (!array.length) {
        return undefined as never;
      }
      return array[generateRandom({ min: 0, max: array.length - 1 })];
    },
    // TODO (Justas): remove this. We have shiftNumber for that
    /**
     * @deprecated use shiftNumber instead
     */
    randomWithBase: (
      base: number,
      offset: number,
      options?: CreateRandomizerArgs['options']
    ): number => {
      const _min = options?.min || min;
      const _max = options?.max || max;
      validate(_min, _max);

      const offsetDirection = random() > 0.5 ? 1 : -1;
      const generatedOffset = random() * offset * offsetDirection;
      const newValue = base + generatedOffset;

      if (_min !== undefined && newValue < _min) {
        return _min;
      }

      if (_max !== undefined && _max < newValue) {
        return _max;
      }

      return newValue;
    },
  };
};

type GenerateTimeSeriesArgs<T extends Record<string, number>> = {
  from: ConvertableToDateTime;
  to: ConvertableToDateTime;
  initial: T;
  nextNumber: (val: number, key: string) => number;
  unit: TimeUnit;
  step?: number;
};

export const generateTimeSeries = <
  T extends Record<string, number> = Record<string, number>
>({
  from,
  to,
  unit,
  initial,
  nextNumber,
  step = 1,
}: GenerateTimeSeriesArgs<T>): ({ timestamp: string } & T)[] => {
  let current: Record<string, number> = initial;
  return timeRange(from, to, { unit, step }).map((date, index) => {
    if (!index) {
      const first = { timestamp: date.toISOString(), ...current } as {
        timestamp: string;
      } & T;
      return first;
    }
    const shifted = Object.keys(current).reduce((acc, curr: string) => {
      acc[curr] = nextNumber(current[curr], curr);
      return acc;
    }, {} as Record<string, number>);
    const result = {
      timestamp: date.toISOString(),
      ...shifted,
    } as { timestamp: string } & T;
    current = shifted;
    return result;
  });
};

type RandomNumberSequenceArgs = {
  token: string;
  initialNumber?: number;
  offset?: number;
  percentageOffset?: number;
  min?: number;
  max?: number;
};

export function* randomNumberSequence({
  initialNumber,
  token,
  offset,
  percentageOffset,
  min,
  max,
}: RandomNumberSequenceArgs): Generator<number> {
  const { random } = createRandomizer({ token, options: { min, max } });
  let current = initialNumber ?? random();
  while (true) {
    const result = current;
    current = shiftNumber({
      value: current,
      min,
      max,
      random,
      offset,
      percentageOffset,
    });
    yield result;
  }
}

type MetricConstraints = {
  trend?: 'linear' | 'exponential';
  noise?: {
    type: 'uniform' | 'normal';
    amplitude: number;
  };
  min?: number;
  max?: number;
  clamp?: boolean;
};

type MultiMetricDatapoint = {
  timestamp: string;
  [key: string]: unknown;
};

type SegmentConstraintsPerKey = Record<string, MetricConstraints>;

type GenerateConstrainedTimeSeriesArgs<DATA extends MultiMetricDatapoint> = {
  knownPoints: Array<
    DATA & {
      nextConstraints?: SegmentConstraintsPerKey;
    }
  >;
  randomizer: Randomizer;
  constraintsByKey?: SegmentConstraintsPerKey;
  defaultConstraints?: MetricConstraints;
  unit?: TimeUnit;
  step?: number;
};

// Standard normal distribution using Box–Muller. Whatever that is, ChatGPT.
export const generateNormal = (randomizer: Randomizer): number => {
  let u = 0;
  while (u === 0) {
    u = randomizer.random({ min: 0, max: 1, fractional: true });
  }
  let v = 0;
  while (v === 0) {
    v = randomizer.random({ min: 0, max: 1, fractional: true });
  }
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
};

export const generateConstrainedTimeSeries = <
  DATA extends MultiMetricDatapoint
>({
  knownPoints,
  randomizer,
  constraintsByKey = {},
  defaultConstraints = {},
  unit = 'minute',
  step = 60,
}: GenerateConstrainedTimeSeriesArgs<DATA>): DATA[] => {
  const result: MultiMetricDatapoint[] = [];

  for (let i = 0; i < knownPoints.length - 1; i++) {
    const start = knownPoints[i];
    const end = knownPoints[i + 1];

    const timestamps = timeRange(start.timestamp, end.timestamp, {
      unit,
      step,
    }).map((d) => d.toISOString());

    const steps = timestamps.length - 1;

    timestamps.forEach((timestamp, idx) => {
      const ratio = steps === 0 ? 0 : idx / steps;

      const point: MultiMetricDatapoint = { timestamp };

      const allKeys = new Set([...Object.keys(start), ...Object.keys(end)]);

      for (const key of allKeys) {
        if (key === 'timestamp') {
          continue;
        }

        const startVal = start[key];
        const endVal = end[key];

        if (typeof startVal === 'number' && typeof endVal === 'number') {
          const constraints: MetricConstraints = {
            ...defaultConstraints,
            ...constraintsByKey?.[key],
            ...start.nextConstraints?.[key],
          };

          const {
            trend = 'linear',
            noise,
            min,
            max,
            clamp = true,
          } = constraints;

          let interpolated =
            trend === 'exponential' && startVal !== 0
              ? startVal * Math.pow(endVal / startVal, ratio)
              : startVal + (endVal - startVal) * ratio;

          // Add noise
          if (noise) {
            const n =
              noise.type === 'normal'
                ? generateNormal(randomizer) * noise.amplitude
                : randomizer.random({ min: -1, max: 1, fractional: true }) *
                  noise.amplitude;
            interpolated += n;
          }

          // Clamp
          if (clamp) {
            if (min !== undefined) {
              interpolated = Math.max(min, interpolated);
            }
            if (max !== undefined) {
              interpolated = Math.min(max, interpolated);
            }
          }

          point[key] = interpolated;
        } else if (timestamp === start.timestamp && Object.hasOwn(start, key)) {
          // Copy non-numeric values only for exact known datapoints
          point[key] = structuredClone(startVal);
        }
      }

      result.push(point);
    });
  }

  return result as DATA[];
};
